import * as signalR from '@microsoft/signalr'
import type { CommandResponse, TelephonyEvent, TelephonyFrame } from '../contract'
import { CloudRoutes, ClientMethod } from './hub'
import type { ConnectionStatus, SendCommand, SourceConfig, TelephonySource } from './types'
import type { CallHistoryEntry } from './history'

/** Live cloud-backed source, per contract v1 section 6 (Azure SignalR serverless).
 *  Events arrive over SignalR (`telephonyFrame`); commands go over HTTP POST. Command
 *  names + event DTOs are authoritative (../contract); this transport now matches the
 *  backend's cloud Functions bridge. */
export class SignalRSource implements TelephonySource {
  private conn?: signalR.HubConnection
  private eventHandlers = new Set<(e: TelephonyEvent) => void>()
  private statusHandlers = new Set<(s: ConnectionStatus) => void>()
  private _status: ConnectionStatus = 'disconnected'

  constructor(private cfg: SourceConfig) {}

  async start(): Promise<void> {
    try {
      await this.connect()
    } catch (err) {
      this.setStatus('disconnected')
      throw err
    }
  }

  private async connect(): Promise<void> {
    this.setStatus('connecting')
    const token = await this.token()
    // 1. Negotiate -> Azure SignalR connection info.
    const negRes = await fetch(
      `${this.cfg.apiBase}${CloudRoutes.negotiate}?siteGuid=${encodeURIComponent(this.cfg.siteGuid ?? '')}`,
      { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {} }
    )
    if (!negRes.ok) throw new Error(`negotiate failed: ${negRes.status}`)
    const info = (await negRes.json()) as { url: string; accessToken: string }
    // 2. Connect; subscribe to the coalesced frame stream.
    this.conn = new signalR.HubConnectionBuilder()
      .withUrl(info.url, { accessTokenFactory: () => info.accessToken })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()
    this.conn.on(ClientMethod.frame, (frame: TelephonyFrame) => {
      if (!frame?.events) return
      for (const e of frame.events) for (const h of this.eventHandlers) h(e)
    })
    this.conn.onreconnecting(() => this.setStatus('reconnecting'))
    this.conn.onreconnected(() => this.setStatus('connected'))
    this.conn.onclose(() => this.setStatus('disconnected'))
    await this.conn.start()
    this.setStatus('connected')
  }

  async stop(): Promise<void> {
    await this.conn?.stop()
    this.setStatus('disconnected')
  }

  onEvent(handler: (e: TelephonyEvent) => void): () => void {
    this.eventHandlers.add(handler); return () => this.eventHandlers.delete(handler)
  }
  onStatus(handler: (s: ConnectionStatus) => void): () => void {
    this.statusHandlers.add(handler); return () => this.statusHandlers.delete(handler)
  }
  status(): ConnectionStatus { return this._status }

  // Commands are HTTP POSTs (serverless SignalR has no hub-server invoke path).
  async sendCommand(cmd: SendCommand): Promise<CommandResponse> {
    const token = await this.token()
    const res = await fetch(`${this.cfg.apiBase}${CloudRoutes.command}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ site_guid: this.cfg.siteGuid, command: cmd.command, params: cmd.params ?? {} })
    })
    return (await res.json()) as CommandResponse
  }

  // Pull recent call history. Endpoint exists server-side; returns [] until it's deployed +
  // CORS-enabled (so the panel degrades gracefully instead of throwing).
  async fetchCallHistory(): Promise<CallHistoryEntry[]> {
    try {
      const token = await this.token()
      const res = await fetch(`${this.cfg.apiBase}/stationbridge/call-history`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      if (!res.ok) return []
      const data = (await res.json()) as { entries?: CallHistoryEntry[] } | CallHistoryEntry[]
      return Array.isArray(data) ? data : (data.entries ?? [])
    } catch {
      return []
    }
  }

  private async token(): Promise<string> {
    return this.cfg.accessTokenFactory ? await this.cfg.accessTokenFactory() : ''
  }
  private setStatus(s: ConnectionStatus): void {
    this._status = s
    for (const h of this.statusHandlers) h(s)
  }
}
